import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Building2, Home, TrendingDown, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DeRiskAnalysisPanel from '../components/DeRiskAnalysisPanel';

// Generate CRE assets
const generateCREAssets = () => {
  const cre_images = [
    'https://images.unsplash.com/photo-1760246964044-1384f71665b9?w=800',
    'https://images.unsplash.com/photo-1773205983024-58964201b593?w=800',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800',
    'https://images.unsplash.com/photo-1464938050520-ef2270bb8ce8?w=800',
  ];
  
  const locations = [
    'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Miami, FL',
    'Denver, CO', 'San Francisco, CA', 'Dallas, TX', 'Atlanta, GA', 'Boston, MA',
    'Tampa, FL', 'Orlando, FL', 'Phoenix, AZ', 'Seattle, WA', 'Portland, OR'
  ];
  
  const types = ['Office Building', 'Retail Center', 'Industrial Warehouse', 'Mixed-Use Development'];
  const statuses = ['Available', 'Pending Review', 'Under Evaluation', 'Available'];
  
  const assets = [];
  for (let i = 1; i <= 50; i++) {
    let baseValue = i <= 30 ? 500000 + (i * 150000) : 5000000 + ((i - 30) * 2000000);
    const isDistressed = Math.random() < 0.3;
    
    assets.push({
      id: `cre-${i}`,
      name: `${types[i % types.length]} - ${locations[i % locations.length].split(',')[0]}`,
      type: 'Commercial Real Estate',
      category: 'cre',
      estimatedValue: baseValue,
      location: locations[i % locations.length],
      status: isDistressed ? 'Distressed' : statuses[i % statuses.length],
      isDistressed,
      description: `Prime commercial real estate opportunity in ${locations[i % locations.length]}. This ${types[i % types.length].toLowerCase()} features modern amenities, excellent location, and strong rental history.`,
      image: cre_images[i % cre_images.length],
      sqft: Math.floor(Math.random() * 150000) + 10000,
      yearBuilt: Math.floor(Math.random() * 30) + 1995,
      occupancyRate: isDistressed ? Math.floor(Math.random() * 30) + 50 : Math.floor(Math.random() * 20) + 80
    });
  }
  return assets;
};

// Generate Foreclosure assets
const generateForeclosureAssets = () => {
  const foreclosure_images = [
    'https://images.pexels.com/photos/101808/pexels-photo-101808.jpeg?w=800',
    'https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?w=800',
    'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800',
    'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
  ];
  
  const locations = [
    'Miami, FL', 'Tampa, FL', 'Orlando, FL', 'Jacksonville, FL',
    'Denver, CO', 'Colorado Springs, CO', 'Manhattan, NY', 'Brooklyn, NY',
    'Phoenix, AZ', 'Las Vegas, NV', 'Atlanta, GA', 'Charlotte, NC'
  ];
  
  const types = ['Single Family Home', 'Multi-Family Property', 'Condo', 'Townhouse', 'Duplex'];
  
  const assets = [];
  for (let i = 1; i <= 50; i++) {
    const baseValue = 100000 + (i * 15000);
    
    assets.push({
      id: `foreclosure-${i}`,
      name: `${types[i % types.length]} - ${locations[i % locations.length].split(',')[0]}`,
      type: 'Foreclosure',
      category: 'foreclosure',
      estimatedValue: baseValue,
      location: locations[i % locations.length],
      status: 'Available',
      isDistressed: true,
      description: `Foreclosure property in ${locations[i % locations.length]}. Available at below-market value with potential for rental income or resale.`,
      image: foreclosure_images[i % foreclosure_images.length],
      bedrooms: Math.floor(Math.random() * 4) + 2,
      bathrooms: Math.floor(Math.random() * 3) + 1,
      sqft: Math.floor(Math.random() * 2500) + 1000
    });
  }
  return assets;
};

const allAssets = [...generateCREAssets(), ...generateForeclosureAssets()];

const Marketplace = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAssets, setFilteredAssets] = useState(allAssets);
  const [deRiskProperty, setDeRiskProperty] = useState(null);
  const [showDeRiskPanel, setShowDeRiskPanel] = useState(false);

  useEffect(() => {
    let filtered = allAssets;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(asset => asset.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(asset => 
        asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredAssets(filtered);
  }, [selectedCategory, searchQuery]);

  const categories = [
    { id: 'all', name: 'All Assets', count: allAssets.length, icon: Building2 },
    { id: 'cre', name: 'Commercial Real Estate', count: 50, icon: Building2 },
    { id: 'foreclosure', name: 'Foreclosure Properties', count: 50, icon: Home },
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2A2A33] mb-4">Asset Marketplace</h1>
            <p className="text-lg text-[#6B6B76]">Browse 100+ distressed assets available for Bitcoin-backed acquisition</p>
          </div>

          {/* Search */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B6B76] h-5 w-5" />
              <Input 
                type="text"
                placeholder="Search by name, location, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#F7F7F7] border-gray-200 text-[#2A2A33] placeholder-[#6B6B76] focus:border-[#006AFF]"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-[#006AFF] text-white shadow-lg'
                    : 'bg-white text-[#2A2A33] border border-gray-200 hover:border-[#006AFF]'
                }`}
              >
                <category.icon className={`h-5 w-5 mr-2 ${selectedCategory === category.id ? 'text-white' : 'text-[#006AFF]'}`} />
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          {/* Info Banner */}
          <div className="bg-[#E8F4FD] border border-[#006AFF]/20 rounded-xl p-4 mb-6 flex items-center">
            <TrendingDown className="h-5 w-5 text-[#006AFF] mr-3" />
            <p className="text-[#2A2A33]">
              <strong>Looking for Real CRE Properties?</strong>{' '}
              <button onClick={() => navigate('/real-cre')} className="text-[#006AFF] underline hover:no-underline">
                Browse 132 verified properties from Florida, Colorado & NYC →
              </button>
            </p>
          </div>

          {/* Asset Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssets.map(asset => (
              <div 
                key={asset.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-[#006AFF] transition-all cursor-pointer group"
                onClick={() => navigate(`/asset/${asset.id}`)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={asset.image} 
                    alt={asset.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      asset.isDistressed 
                        ? 'bg-red-100 text-red-600' 
                        : asset.status === 'Available' 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {asset.isDistressed ? 'Distressed' : asset.status}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="text-sm text-[#006AFF] font-medium mb-2">{asset.type}</div>
                  <h3 className="text-xl font-bold text-[#2A2A33] mb-2 line-clamp-1">{asset.name}</h3>
                  <div className="flex items-center text-[#6B6B76] text-sm mb-3">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{asset.location}</span>
                  </div>
                  <p className="text-[#6B6B76] text-sm mb-4 line-clamp-2">{asset.description}</p>
                  
                  {asset.category === 'cre' && (
                    <div className="flex gap-4 text-xs text-[#6B6B76] mb-4">
                      <span>{asset.sqft?.toLocaleString()} SF</span>
                      <span>Built {asset.yearBuilt}</span>
                      <span>{asset.occupancyRate}% Occupied</span>
                    </div>
                  )}
                  
                  {asset.category === 'foreclosure' && (
                    <div className="flex gap-4 text-xs text-[#6B6B76] mb-4">
                      <span>{asset.bedrooms} Beds</span>
                      <span>{asset.bathrooms} Baths</span>
                      <span>{asset.sqft?.toLocaleString()} SF</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-xs text-[#6B6B76] mb-1">Estimated Value</div>
                        <div className="text-2xl font-bold text-[#2A2A33]">
                          {asset.estimatedValue >= 1000000 
                            ? `$${(asset.estimatedValue / 1000000).toFixed(2)}M`
                            : `$${(asset.estimatedValue / 1000).toFixed(0)}K`
                          }
                        </div>
                      </div>
                      <Button 
                        onClick={(e) => { e.stopPropagation(); navigate(`/asset/${asset.id}`); }}
                        className="bg-[#006AFF] hover:bg-[#0055CC] text-white"
                      >
                        View Details
                      </Button>
                    </div>
                    {asset.isDistressed && (
                      <Button 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setDeRiskProperty({
                            id: asset.id,
                            name: asset.name,
                            property_type: asset.type,
                            address: asset.location,
                            status: asset.status,
                            loan_amount: asset.estimatedValue * 0.7,
                            ltv_ratio: 75 + Math.floor(Math.random() * 20),
                            occupancy_rate: asset.occupancyRate || 60,
                            dscr: 0.5 + Math.random() * 0.5,
                            credIQScore: ['D-', 'D', 'D+', 'C-', 'C'][Math.floor(Math.random() * 5)],
                            noi: asset.estimatedValue * 0.06,
                            btcLendingRate: 7 + Math.random() * 4
                          });
                          setShowDeRiskPanel(true); 
                        }}
                        className="w-full bg-gradient-to-r from-[#006AFF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#004499] text-white text-sm"
                        data-testid={`derisk-btn-${asset.id}`}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        AI De-Risk Analysis
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredAssets.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-[#6B6B76] text-lg">No assets found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* De-Risk Analysis Panel */}
      <DeRiskAnalysisPanel 
        property={deRiskProperty}
        isOpen={showDeRiskPanel}
        onClose={() => { setShowDeRiskPanel(false); setDeRiskProperty(null); }}
      />

      <Footer />
    </div>
  );
};

export default Marketplace;
